import { FormEvent, useMemo, useState } from 'react';
import { Edit, Plus, RefreshCw, Trash2 } from 'lucide-react';
import Button from '@/components/ui/Button';
import DataTable from '@/components/ui/DataTable';
import Input from '@/components/ui/Input';
import Modal from '@/components/ui/Modal';
import { TableColumn } from '@/types';
import TVChannelScrapingLayout from '@/pages/content/TVChannelScrapingLayout';
import {
  useChannelScrapingServers,
  useCreateChannelScrapingServer,
  useDeleteChannelScrapingServer,
  useUpdateChannelScrapingServer,
} from '@/hooks/useChannelScraping';
import { useSelectedScrapingServer } from '@/hooks/useSelectedScrapingServer';
import { ChannelScrapingServer } from '@/services/channelScrapingService';

interface ServerFormState {
  name: string;
  url: string;
  username: string;
  password: string;
}

const emptyForm: ServerFormState = {
  name: '',
  url: '',
  username: '',
  password: '',
};

const TVChannelScrapingServers = () => {
  const { selectedScrapingServerId } = useSelectedScrapingServer();
  const { data: servers = [], isLoading, isError, error, refetch, isFetching } = useChannelScrapingServers(selectedScrapingServerId);
  const createMutation = useCreateChannelScrapingServer();
  const updateMutation = useUpdateChannelScrapingServer();
  const deleteMutation = useDeleteChannelScrapingServer();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingServer, setEditingServer] = useState<ChannelScrapingServer | null>(null);
  const [form, setForm] = useState<ServerFormState>(emptyForm);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  const sortedServers = useMemo(() => [...servers].sort((a, b) => a.name.localeCompare(b.name)), [servers]);

  const openCreateModal = () => {
    setEditingServer(null);
    setForm(emptyForm);
    setSubmitError(null);
    setIsModalOpen(true);
  };

  const openEditModal = (server: ChannelScrapingServer) => {
    setEditingServer(server);
    setForm({
      name: server.name,
      url: server.url,
      username: server.username || '',
      password: '',
    });
    setSubmitError(null);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    if (isSubmitting) return;
    setIsModalOpen(false);
    setEditingServer(null);
    setForm(emptyForm);
    setSubmitError(null);
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitError(null);
    try {
      if (editingServer) {
        await updateMutation.mutateAsync({
          id: editingServer.id,
          data: form,
          scrapingServerId: selectedScrapingServerId,
        });
      } else {
        await createMutation.mutateAsync({ data: form, scrapingServerId: selectedScrapingServerId });
      }
      closeModal();
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : 'No se pudo guardar el servidor');
    }
  };

  const handleDelete = async (server: ChannelScrapingServer) => {
    if (!window.confirm(`¿Eliminar servidor ${server.name}?`)) {
      return;
    }
    await deleteMutation.mutateAsync({ id: server.id, scrapingServerId: selectedScrapingServerId });
  };

  const columns: TableColumn<ChannelScrapingServer>[] = [
    { key: 'name', header: 'Nombre', accessor: (server) => <span className="font-medium text-gray-900">{server.name}</span> },
    {
      key: 'url',
      header: 'URL',
      accessor: (server) => <span className="block max-w-md truncate text-sm text-gray-900" title={server.url}>{server.url}</span>,
    },
    { key: 'username', header: 'Usuario', accessor: (server) => <span className="text-sm text-gray-900">{server.username || '-'}</span> },
    {
      key: 'enabled',
      header: 'Estado',
      accessor: (server) => (
        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${
          server.enabled === false ? 'bg-gray-100 text-gray-700' : 'bg-green-100 text-green-800'
        }`}>
          {server.enabled === false ? 'Inactivo' : 'Activo'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: 'Acciones',
      align: 'center',
      accessor: (server) => (
        <div className="flex items-center justify-center gap-2">
          <Button size="sm" variant="outline" onClick={() => openEditModal(server)}>
            <Edit className="h-4 w-4" />
          </Button>
          <Button size="sm" variant="danger" onClick={() => void handleDelete(server)} disabled={deleteMutation.isPending}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ),
      className: 'w-32',
    },
  ];

  return (
    <TVChannelScrapingLayout>
      <div className="space-y-4">
        {isError && (
          <div className="rounded-md bg-red-50 p-4 text-sm text-red-700">
            {error instanceof Error ? error.message : 'Error al cargar servidores Flussonic'}
          </div>
        )}

        {isLoading ? (
          <div className="border border-gray-200 bg-white px-4 py-8 text-center text-sm text-gray-500 shadow">
            Cargando servidores Flussonic...
          </div>
        ) : (
          <DataTable
            data={sortedServers}
            columns={columns}
            paginate={false}
            tableStyle="compact"
            showToolbar={false}
            className="shadow"
            headerRenderer={() => (
              <div className="border-b border-gray-200 bg-white">
                <div className="flex flex-col gap-3 px-4 py-2 md:flex-row md:items-center md:justify-between">
                  <h3 className="text-lg font-medium text-gray-900">Servidores Flussonic</h3>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" onClick={() => refetch()} disabled={isFetching}>
                      <RefreshCw className={`mr-1 h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
                      Refrescar
                    </Button>
                    <Button size="sm" onClick={openCreateModal} className="bg-indigo-600 hover:bg-indigo-700 text-white">
                      <Plus className="mr-1 h-4 w-4" />
                      Nuevo servidor Flussonic
                    </Button>
                  </div>
                </div>
              </div>
            )}
          />
        )}

        <Modal
          isOpen={isModalOpen}
          onClose={closeModal}
          title={editingServer ? 'Editar servidor Flussonic' : 'Nuevo servidor Flussonic'}
          size="lg"
          closeOnBackdrop={!isSubmitting}
          closeOnEsc={!isSubmitting}
        >
          <form onSubmit={handleSubmit} className="space-y-4 px-2 py-2">
            <Input label="Nombre" value={form.name} onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))} required />
            <Input label="URL" value={form.url} onChange={(e) => setForm((prev) => ({ ...prev, url: e.target.value }))} required />
            <Input label="Usuario" value={form.username} onChange={(e) => setForm((prev) => ({ ...prev, username: e.target.value }))} required />
            <Input label={editingServer ? 'Contraseña nueva' : 'Contraseña'} type="password" value={form.password} onChange={(e) => setForm((prev) => ({ ...prev, password: e.target.value }))} required={!editingServer} />
            {submitError && <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">{submitError}</div>}
            <div className="flex justify-end gap-2 border-t border-gray-200 pt-4">
              <Button type="button" variant="outline" onClick={closeModal} disabled={isSubmitting}>Cancelar</Button>
              <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700 text-white" disabled={isSubmitting}>Guardar</Button>
            </div>
          </form>
        </Modal>
      </div>
    </TVChannelScrapingLayout>
  );
};

export default TVChannelScrapingServers;
